/**
 * The owner-facing Notifications page's feed, plus per-item read state.
 *
 * Data comes from the owner_notification_feed() RPC (20260826093000) rather
 * than a client-side select: `apartments` has no owner-facing SELECT policy
 * at all, and `bookings`' only SELECT policy excludes owners by name — see
 * that migration's own comment. The RPC does the ownership check server-side
 * and hands back only what this owner may see.
 *
 * Read state is a TABLE (owner_notification_reads, 20261012085011), not
 * localStorage. It used to be per browser, which meant an owner who read their
 * notifications on a laptop still saw the badge on their phone — and the
 * sidebar's count and this page's count were both right about different
 * machines. A row per item, not a single lastRead cutoff, because the page
 * lets an owner mark one older item read while leaving newer ones unread.
 *
 * Marking is optimistic: the write is a round trip now, and a badge that waits
 * for it before clearing reads as a page that did not register the click. A
 * failure rolls the cache back, so the item returns to unread rather than
 * silently diverging from the server.
 */
import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import {
  buildOwnerNotificationFeed,
  type OwnerNotificationEntry,
} from "@/lib/owner-notification-feed";

/** One frozen empty set, so `readIds` is referentially stable while loading
 *  and the memo below does not recompute on every render. */
const EMPTY_READS: ReadonlySet<string> = new Set<string>();

export interface OwnerNotificationRow extends OwnerNotificationEntry {
  unread: boolean;
}

/**
 * Keyed by account, as accountBillingAddressKey already is. Neither sign-out
 * nor impersonation clears the QueryClient, so an unscoped key hands the next
 * account the previous owner's rows while its own request is in flight — and
 * impersonation is exactly how this portal is meant to be reviewed first.
 */
export function ownerNotificationReadsKey(userId: string | undefined) {
  return ["owner", "notification-reads", userId] as const;
}

/**
 * True when the failure is "this database has no such table" — the dev project
 * trails LIVE. The caller degrades to "nothing is read", which shows every
 * item as unread rather than breaking the page.
 */
function isMissingRelation(error: {
  code?: string;
  message?: string;
}): boolean {
  if (error?.code === "42P01" || error?.code === "PGRST205") return true;
  return /does not exist|could not find the table/i.test(error?.message ?? "");
}

interface FeedRpcResult {
  apartments: Array<{
    id: number;
    label: string | null;
    portal_ready_owner_email_sent_at: string | null;
  }>;
  bookings: Array<{
    id: number;
    name: string | null;
    apartment_label: string | null;
    room_title: string | null;
    monthly_rent: number | null;
    deposit_amount: number | null;
    lease_signed_owner_email_sent_at: string | null;
    deposit_received_owner_email_sent_at: string | null;
    keys_handed_over_owner_email_sent_at: string | null;
    move_in_protocol_owner_email_sent_at: string | null;
    move_out_date: string | null;
    move_out_date_owner_email_sent_at: string | null;
    move_out_protocol_owner_email_sent_at: string | null;
    keys_returned_owner_email_sent_at: string | null;
  }>;
  payments: Array<{
    id: string;
    apartment_label: string | null;
    tenant_name: string | null;
    amount_cents: number;
    rent_received_owner_email_sent_at: string | null;
  }>;
}

export function useOwnerNotifications(enabled: boolean = true) {
  const { user } = useAuth();
  const qc = useQueryClient();

  const feedQuery = useQuery({
    queryKey: ["v2", "owner-notifications", "feed", user?.id],
    enabled: enabled && !!user?.id,
    queryFn: async (): Promise<{
      entries: OwnerNotificationEntry[];
      properties: string[];
    }> => {
      const { data, error } = await supabase.rpc("owner_notification_feed");
      if (error) throw error;
      // The RPC returns jsonb; its SQL body builds this exact shape.
      const result = (data ?? {
        apartments: [],
        bookings: [],
        payments: [],
      }) as unknown as FeedRpcResult;
      // Every apartment the owner holds, not just ones with a logged event —
      // the RPC already scopes v_apartments to all of them (see its SQL), so
      // the dropdown can list every property up front instead of only those
      // that happen to have sent mail already. A missing label falls back to
      // "—", matching buildOwnerNotificationFeed's own fallback, so a row
      // tagged "—" stays selectable instead of disappearing from the filter.
      const properties = Array.from(
        new Set(result.apartments.map((a) => a.label ?? "—"))
      ).sort();
      return { entries: buildOwnerNotificationFeed(result), properties };
    },
  });

  // Its own query, not part of the feed's: the feed is an expensive RPC over
  // apartments, bookings and payments, and marking one item read must not
  // re-run it.
  const readsQuery = useQuery({
    queryKey: ownerNotificationReadsKey(user?.id),
    enabled: enabled && !!user?.id,
    queryFn: async (): Promise<Set<string>> => {
      const { data, error } = await supabase
        .from("owner_notification_reads")
        .select("notification_id");
      if (error) {
        if (isMissingRelation(error)) return new Set<string>();
        throw error;
      }
      return new Set(
        ((data ?? []) as Array<{ notification_id: string }>).map(
          (r) => r.notification_id
        )
      );
    },
  });

  const readIds = readsQuery.data ?? EMPTY_READS;

  const rows: OwnerNotificationRow[] = useMemo(
    () =>
      (feedQuery.data?.entries ?? []).map((entry) => ({
        ...entry,
        unread: !readIds.has(entry.id),
      })),
    [feedQuery.data, readIds]
  );

  const properties = feedQuery.data?.properties ?? [];

  const unreadCount = useMemo(
    () => rows.reduce((n, r) => (r.unread ? n + 1 : n), 0),
    [rows]
  );

  /**
   * Upsert with ignoreDuplicates, which is ON CONFLICT DO NOTHING.
   *
   * NOT the default merge-duplicates: that is ON CONFLICT DO UPDATE, and
   * Postgres checks the UPDATE privilege for it at statement level whether or
   * not a row actually conflicts — so with UPDATE revoked (which the migration
   * does deliberately: a read stamp is a fact, not something to rewrite) every
   * mark-as-read would have been refused, including the first one. The SQL
   * suite asserts that revoke; this is the client half of the same decision.
   */
  const markMutation = useMutation({
    mutationFn: async (ids: string[]): Promise<void> => {
      if (!user?.id || ids.length === 0) return;
      const { error } = await supabase.from("owner_notification_reads").upsert(
        ids.map((notification_id) => ({
          user_id: user.id,
          notification_id,
        })),
        { onConflict: "user_id,notification_id", ignoreDuplicates: true }
      );
      if (error) throw error;
    },
    // Optimistic: the badge clears on the click, not on the round trip.
    onMutate: async (ids: string[]) => {
      await qc.cancelQueries({ queryKey: ownerNotificationReadsKey(user?.id) });
      const previous = qc.getQueryData<Set<string>>(
        ownerNotificationReadsKey(user?.id)
      );
      qc.setQueryData<Set<string>>(
        ownerNotificationReadsKey(user?.id),
        new Set([...(previous ?? []), ...ids])
      );
      return { previous };
    },
    onError: (_err, _ids, context) => {
      // Back to what the server last said, so a failed write does not leave
      // the page claiming something was read when it was not.
      if (context?.previous !== undefined) {
        qc.setQueryData(ownerNotificationReadsKey(user?.id), context.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: ownerNotificationReadsKey(user?.id),
      });
    },
  });

  const markRead = useCallback(
    (id: string) => {
      markMutation.mutate([id]);
    },
    [markMutation]
  );

  const markAllRead = useCallback(() => {
    const unread = rows.filter((r) => r.unread).map((r) => r.id);
    if (unread.length > 0) markMutation.mutate(unread);
  }, [markMutation, rows]);

  return {
    rows,
    properties,
    unreadCount,
    isLoading: feedQuery.isLoading,
    isError: feedQuery.isError,
    error: feedQuery.error,
    refetch: feedQuery.refetch,
    markRead,
    markAllRead,
  };
}

/** Unread count only — for the nav badge. Pass `isOwnerPortalOnly` as
 *  `enabled` so a staff session never fires this query at all. */
export function useOwnerUnreadNotificationCount(enabled: boolean): number {
  const { unreadCount } = useOwnerNotifications(enabled);
  return unreadCount;
}
